import React from "react";
import _ from "lodash";
import ACLStore from "lib/aclStore";
import { Avatar, Tag } from "@toddle-edu/ds-web";
//Constant imports
import {
  ELEMENT_TYPE_UBD_LEARNING_STANDARD,
  ELEMENT_TYPE_UBD_ATL,
  ELEMENT_TYPE_DP_OBJECTIVES,
  ELEMENT_TYPE_DP_SUBJECT_STANDARD,
  ELEMENT_TYPE_UBD_SUBJECT,
  ELEMENT_TYPE_DP_LEARNING_STANDARD,
  ELEMENT_TYPE_DP_SUBJECT,
  ELEMENT_TYPE_PYP_ATL,
  ELEMENT_TYPE_PYP_LEARNER_PROFILE,
  ELEMENT_TYPE_PYP_KEY_CONCEPT,
  ELEMENT_TYPE_MYP_LEARNING_STANDARD,
  ELEMENT_TYPE_MYP_SUBJECT,
  ELEMENT_TYPE_PYP_BENCHMARK,
  ELEMENT_TYPE_MYP_ATL,
  GRADING_PERIOD_CUMULATIVE,
  SCORE_BASED,
  GRADING_VISIBILITY_SETTINGS,
  STUDENT,
  STAFF,
  CURRICULUM_TYPE_MYP,
  MYP_CRITERIA_TOTAL,
  MYP_IB_DEFINED,
  MYP_FINAL_GRADE,
  ELEMENT_TYPE_GRADING_PERIOD_OVERVIEW,
  CURRICULUM_TYPE_DP,
  DP_IB_DEFINED,
} from "Constants/stringConstants";

//Util imports
import {
  generateTreeOfNode,
  htmlToText,
  getArchivedSortedSubjectListMemoized,
} from "Utils";
import { uniqlyCombineGradeDetails } from "Course/modules/CourseGraphqlHelpers";
import {
  STUDENT_PROGRESS_SUMMARY_DETAILS_MAP,
  STUDENT_STANDARDS_GRADEBOOK,
} from "./StudentProgressSummaryConstants";
import { getAvatarData } from "LearningCourses/modules/Utils";
import { getDefaultVisibilityForType } from "GradingSetupV2/modules/Utils";
import { STANDARDS_TYPES_BY_CURRICULUM } from "Gradebook/modules/Constants/stringConstants";
import { getFinalGradeCalculationSettings } from "GradingConfigurations/modules/Utils";
import { FINAL_GRADE_CALCULATION_METHODOLOGY_UID } from "GradingConfigurations/modules/Constants";

export const getAssociatedPlannerFilters = _.memoize(
  ({ plannerElementType, studentGradeIds, studentSubjectIds }) => {
    const plannerFilters = { associatedEntities: [], associatedParents: [] };
    const addFilterToPlannerFilters = ({ ids, type, filterType }) => {
      if (!_.isEmpty(ids)) {
        switch (filterType) {
          case "associatedEntities": {
            plannerFilters.associatedEntities.push({
              ids,
              type,
            });
            break;
          }
          case "associatedParents": {
            plannerFilters.associatedParents.push({ ids, type });
            break;
          }
        }
      }
    };
    switch (plannerElementType) {
      case ELEMENT_TYPE_UBD_LEARNING_STANDARD:
      case ELEMENT_TYPE_DP_SUBJECT_STANDARD:
      case ELEMENT_TYPE_MYP_LEARNING_STANDARD: {
        addFilterToPlannerFilters({
          ids: studentSubjectIds,
          type: "SUBJECT",
          filterType: "associatedParents",
        });
        addFilterToPlannerFilters({
          ids: studentGradeIds,
          type: "GRADE",
          filterType: "associatedParents",
        });
        break;
      }
      case ELEMENT_TYPE_UBD_ATL:
      case ELEMENT_TYPE_MYP_ATL: {
        addFilterToPlannerFilters({
          ids: studentGradeIds,
          type: "GRADE",
          filterType: "associatedParents",
        });
        break;
      }
      case ELEMENT_TYPE_DP_OBJECTIVES: {
        addFilterToPlannerFilters({
          ids: studentSubjectIds,
          type: "SUBJECT",
          filterType: "associatedParents",
        });
        break;
      }
    }
    const modifiedPlannerFilters = _.pickBy(
      plannerFilters,
      value => !_.isEmpty(value)
    );
    return modifiedPlannerFilters;
  }
);

const getPlannerNodeChildren = ({ node, plannerElementsNode }) => {
  const nodeChildren = node?.children || [];

  let children = _.filter(plannerElementsNode, item =>
    _.includes(nodeChildren, item?.id)
  );

  if (!_.isEmpty(children)) {
    children = _.map(children, child => {
      if (_.isEmpty(child?.children)) {
        return child;
      }
      return getPlannerNodeChildren({ node: child, plannerElementsNode });
    });
  }
  return {
    ...node,
    children,
  };
};
//Only add the mapping here if the subject node is to be manually added to the planner element list
const subjectWithSubjectNodeMapping = {
  [ELEMENT_TYPE_UBD_LEARNING_STANDARD]: ELEMENT_TYPE_UBD_SUBJECT,
  [ELEMENT_TYPE_DP_LEARNING_STANDARD]: ELEMENT_TYPE_DP_SUBJECT,
  [ELEMENT_TYPE_MYP_LEARNING_STANDARD]: ELEMENT_TYPE_MYP_SUBJECT,
};

export const getSubjectNode = ({
  filteredPlannerElementsNodes,
  minDepth,
  selectedRootNode,
  type,
}) => {
  const children = _.filter(filteredPlannerElementsNodes, {
    depth: minDepth,
  });

  return {
    id: selectedRootNode?.key,
    label: selectedRootNode?.label,
    isSubjectArchived: selectedRootNode?.isArchived,
    parent: null,
    type,
    children: _.map(children, "id"),
    isLeaf: false,
    depth: 0,
    elementParentId: selectedRootNode?.key,
    ibPlannerElementType: type,
  };
};

export const getPlannerElementList = ({
  plannerElementsNode,
  rootNodeId,
  elementType,
  selectedRootNode,
}) => {
  const nodes = _.get(plannerElementsNode, "nodes", []);
  const rootNodes = _.get(plannerElementsNode, "rootNodes", []);
  const rootNode = _.find(nodes, { id: rootNodeId });

  switch (elementType) {
    case ELEMENT_TYPE_PYP_KEY_CONCEPT:
    case ELEMENT_TYPE_PYP_LEARNER_PROFILE:
      return _.map(rootNodes, nodeId => {
        return _.find(nodes, { id: nodeId });
      });
    case ELEMENT_TYPE_PYP_BENCHMARK:
    case ELEMENT_TYPE_PYP_ATL:
      return [
        {
          ...rootNode,
          children: generateTreeOfNode({
            nodes,
            nodeId: rootNodeId,
          }),
        },
      ];
    case ELEMENT_TYPE_UBD_ATL: {
      const node = _.find(plannerElementsNode, { id: rootNodeId });
      return [
        getPlannerNodeChildren({
          node,
          plannerElementsNode,
        }),
      ];
    }
    default: {
      let filteredPlannerElementsNodes = plannerElementsNode;

      if (rootNodeId) {
        filteredPlannerElementsNodes = _.filter(plannerElementsNode, item => {
          const associatedParentsIds = _.map(item?.associatedParents, "id");
          if (_.includes(associatedParentsIds, rootNodeId)) {
            return item;
          }
        });
      }

      let minDepth = _.minBy(filteredPlannerElementsNodes, "depth")?.depth;

      if (_.includes(Object.keys(subjectWithSubjectNodeMapping), elementType)) {
        if (!selectedRootNode) {
          return [];
        }
        const subjectNode = getSubjectNode({
          filteredPlannerElementsNodes,
          minDepth,
          selectedRootNode,
          type: subjectWithSubjectNodeMapping[elementType],
        });
        filteredPlannerElementsNodes.push(subjectNode);
        minDepth = 0;
      }

      const rootNodeElements = _.filter(filteredPlannerElementsNodes, item =>
        _.isEqual(item?.depth, minDepth)
      );

      return _.map(rootNodeElements, node =>
        getPlannerNodeChildren({
          node: node,
          plannerElementsNode: filteredPlannerElementsNodes,
        })
      );
    }
  }
};

export const getBenchmarksRootNodePair = ({ queryData }) => {
  const allGrades = _.get(queryData, "node.allGrades", []);
  const subjects = getArchivedSortedSubjectListMemoized({
    subjectList: _.get(uniqlyCombineGradeDetails(allGrades), "subjects", []),
  });
  return _.reduce(
    subjects,
    (result, subject) => {
      const {
        benchmarkRootNode = {},
        id: subjectId,
        isArchived = false,
      } = subject;
      const { id: rootNodeId, label } = benchmarkRootNode || {};
      if (rootNodeId) {
        result.push({
          label: label,
          key: rootNodeId,
          subject: subjectId,
          isArchived,
        });
      }
      return result;
    },
    []
  );
};

const getPypAtlRootNodePair = ({ plannerElementsNode }) => {
  const { rootNodes, nodes } = plannerElementsNode;
  return _.reduce(
    rootNodes,
    (result, id) => {
      const node = _.find(nodes, { id });
      if (node) {
        return [...result, { label: node.label, key: id }];
      }
      return result;
    },
    []
  );
};

const getUbdSkillsRootNodePair = ({ plannerElementsNode }) => {
  const minDepth = _.minBy(plannerElementsNode, "depth")?.depth;
  return _.reduce(
    plannerElementsNode,
    (result, item) => {
      if (_.isEqual(item?.depth, minDepth)) {
        result.push({
          key: item.id,
          label: item.label,
        });
      }
      return result;
    },
    []
  );
};

const getSubjectRootNodePair = ({ subjects }) => {
  return _.map(subjects, item => ({
    key: item.id,
    label: item.name,
    isArchived: item?.isArchived,
  }));
};

/*This function is used to get filter dropdown options based on the planner element nodes on the basis of current element
 type and also the first option that should be selected by default*/
export const getRootNodePair = ({
  elementType,
  subjects,
  plannerElementsNode,
  allCourseSubjectsIds,
  studentSubjectDetailsQueryData,
  studentSubjectIds,
}) => {
  switch (elementType) {
    case ELEMENT_TYPE_UBD_LEARNING_STANDARD:
    case ELEMENT_TYPE_DP_SUBJECT_STANDARD:
    case ELEMENT_TYPE_MYP_LEARNING_STANDARD:
    case ELEMENT_TYPE_DP_OBJECTIVES: {
      const rootNodePair = getSubjectRootNodePair({ subjects });
      const studentsCourseSubjectIds = _.intersection(
        allCourseSubjectsIds,
        studentSubjectIds
      );
      return {
        rootNodePair,
        initialRootNodeId:
          _.first(studentsCourseSubjectIds) ?? _.first(rootNodePair)?.key,
      };
    }
    case ELEMENT_TYPE_UBD_ATL: {
      const rootNodePair = getUbdSkillsRootNodePair({ plannerElementsNode });
      return {
        rootNodePair,
        initialRootNodeId: _.first(rootNodePair)?.key,
      };
    }
    case ELEMENT_TYPE_PYP_ATL: {
      const rootNodePair = getPypAtlRootNodePair({ plannerElementsNode });
      return { rootNodePair, initialRootNodeId: _.first(rootNodePair)?.key };
    }
    case ELEMENT_TYPE_PYP_BENCHMARK: {
      const rootNodePair = getBenchmarksRootNodePair({
        queryData: studentSubjectDetailsQueryData,
      });
      return { rootNodePair, initialRootNodeId: _.first(rootNodePair)?.key };
    }
    default:
      return [];
  }
};

export const getStudentElementRatingValueOfCriteriaSet = ({
  elementRatingsMap,
  id,
  setId,
}) => {
  const ratings = _.get(elementRatingsMap, [id, setId], []);

  if (_.isEmpty(ratings)) return null;

  if (_.size(ratings) === 1) {
    return _.get(ratings, "0.academicCriteriaValue.id", null);
  }

  const overrideRating = _.find(ratings, { isComputedRating: false });

  return _.get(overrideRating, "academicCriteriaValue.id", null);
};

const getFilteredElement = ({ element, searchText }) => {
  const { children } = element;

  let filteredChildren = _.filter(
    children,
    child =>
      _.includes(_.toLower(htmlToText(child?.label)), _.toLower(searchText)) ||
      !_.isEmpty(child?.children)
  );

  filteredChildren = _.map(filteredChildren, child => {
    return getFilteredElement({ element: child, searchText });
  });

  filteredChildren = _.filter(
    filteredChildren,
    child =>
      _.includes(_.toLower(htmlToText(child?.label)), _.toLower(searchText)) ||
      !_.isEmpty(child?.children)
  );

  return { ...element, children: filteredChildren };
};

export const getFilteredElementListBySearchText = ({
  searchText,
  elementList,
}) => {
  return searchText
    ? _.reduce(
        elementList,
        (updatedList, element) => {
          const filteredElement = getFilteredElement({ element, searchText });
          const { label, children } = filteredElement;
          if (
            _.includes(_.toLower(htmlToText(label)), _.toLower(searchText)) ||
            !_.isEmpty(children)
          ) {
            updatedList.push(filteredElement);
          }
          return updatedList;
        },
        []
      )
    : elementList;
};

export const getFilteredPlannerElementList = ({
  userType,
  searchText,
  elementList,
  gradeScales,
  elementRatingsMap,
  visibilitySettings,
  isVisibilitySettingsV2Enabled,
  isUsingNewGradingSetupInCourses,
}) => {
  const filteredBySearch = getFilteredElementListBySearchText({
    searchText,
    elementList,
  });

  const listVisibility =
    _.get(visibilitySettings, "LIST") || getDefaultVisibilityForType("LIST");

  const showOnlyGradedElements =
    isUsingNewGradingSetupInCourses &&
    isVisibilitySettingsV2Enabled &&
    userType !== STAFF &&
    listVisibility === "GRADED";

  if (!showOnlyGradedElements) {
    return filteredBySearch;
  }

  const filterElementByRatings = element => {
    const { id: elementId, children = [] } = element || {};

    const hasRatings = _.some(gradeScales, gradeScale => {
      const setValue = getStudentElementRatingValueOfCriteriaSet({
        id: elementId,
        elementRatingsMap,
        setId: gradeScale?.id,
      });
      return !_.isNil(setValue);
    });

    const filteredChildren = _.compact(
      _.map(children, child => filterElementByRatings(child))
    );

    const isVisible = hasRatings || !_.isEmpty(filteredChildren);

    return isVisible ? { ...element, children: filteredChildren } : null;
  };

  return _.compact(_.map(filteredBySearch, filterElementByRatings));
};

//Returns the default subject for criteria ratings filter dropdown based on course and student subject ids
export const getInitialSubjectId = ({
  allCourseSubjectsIds,
  studentSubjectIds,
  elementType,
  initialRootNode,
}) => {
  if (elementType === ELEMENT_TYPE_PYP_BENCHMARK) {
    return initialRootNode?.subject;
  }

  const studentsCourseSubjectIds = _.intersection(
    allCourseSubjectsIds,
    studentSubjectIds
  );
  return _.first(studentsCourseSubjectIds) ?? _.first(studentSubjectIds);
};

export const getStudentGradeSummaryCardsData = ({
  studentGradeSummaryData,
  mypCriteriaData,
  containerWidth,
}) => {
  const cardWidth = 176;
  const cardsCountPerRow = Math.max(Math.floor(containerWidth / cardWidth), 1);

  const cardsData = [];

  for (let i = 0; i < mypCriteriaData.length; i += cardsCountPerRow) {
    const rowData = _.slice(mypCriteriaData, i, i + cardsCountPerRow);
    cardsData.push(rowData);
  }

  for (let i = 0; i < studentGradeSummaryData.length; i += cardsCountPerRow) {
    const rowData = _.slice(studentGradeSummaryData, i, i + cardsCountPerRow);
    cardsData.push(rowData);
  }

  return cardsData;
};

export const checkVisualiseGradebookChangesEnabled = () =>
  ACLStore.can("FeatureFlag:VisualiseAssessmentTypeInStudentGradebook");

export const shouldShowSelectedYearMYPCriteriaRatings = () =>
  ACLStore.can("FeatureFlag:ShowSelectedYearMYPCriteriaRatings");

export const getFinalGradesTermsSideBySideViewEnabled = () => {
  return ACLStore.can("FeatureFlag:FinalGradesTermsSideBySideView");
};

export const collectAssociatedEntities = ({ termIds, terms }) => {
  return _.reduce(
    terms,
    (associatedEntities, item) => {
      const associatedTerms = _.get(item, "associatedAcademicTermEntities", []);

      if (
        !_.isEmpty(associatedTerms) &&
        _.includes(termIds, _.get(item, "id", ""))
      ) {
        return [...associatedEntities, ...associatedTerms];
      }

      return associatedEntities;
    },
    []
  );
};

export const getCumulativeTermsString = ({ term }) => {
  const isCumulative =
    _.get(term, "type", "REPORTING") === GRADING_PERIOD_CUMULATIVE;
  const entities = _.get(term, "associatedAcademicTermEntities", []);

  if (!isCumulative || _.isEmpty(entities)) {
    return "";
  }

  const termsString = _.map(entities, entity => {
    const academicTermLabel = _.get(entity, "academicTerm.abbreviation", "");
    const weightage = _.round(
      _.get(entity, "cumulativeAcademicTermWeightages[0].percentage", 0),
      2
    );
    return `${academicTermLabel} ${weightage}%`;
  }).join(" + ");

  return `(${termsString})`;
};

// * filtering OTHERS subject group type with no objectives
export const getFilteredSubjectObjectivesData = _.memoize(
  subjectObjectivesData => {
    return _.filter(subjectObjectivesData, subject => {
      const subjectType = _.get(subject, "type");
      const objectives = _.get(subject, "objectives", []);

      return !_.isEqual(subjectType, "OTHERS") || !_.isEmpty(objectives);
    });
  },
  JSON.stringify
);

// * filtering archived subjects with no assignments created
export const getFilteredArchivedSubjects = (subjects = []) => {
  const filteredSubjects = _.filter(subjects, subject => {
    const isArchived = _.get(subject, "isArchived", false);
    const assignmentCount = _.get(subject, "assignmentCount", 0);

    return (
      (!isArchived || assignmentCount > 0) &&
      !_.includes(["CAS", "EE"], subject?.type)
    );
  });

  return filteredSubjects;
};

export const getStudentProgressSummaryDetails = ({
  curriculumType,
  userType,
}) => {
  let studentProgressSummaryDetails = _.get(
    STUDENT_PROGRESS_SUMMARY_DETAILS_MAP,
    [curriculumType, "summaryScreenDetails"],
    []
  );

  if (
    userType === "staff" &&
    ACLStore.can("FeatureFlag:ShowAllAcademicYearsInStudentGBForAdmin")
  ) {
    studentProgressSummaryDetails = _.map(
      studentProgressSummaryDetails,
      detail => ({
        ...detail,
        showAcademicYearsFilter: true,
      })
    );
  }

  return studentProgressSummaryDetails;
};

export const getSubjectGradingConfigVariables = ({
  subjectsList,
  yearGroupGradeId,
  academicYearId,
}) => {
  const subjectIds = [];
  const gradeIds = [];

  _.forEach(subjectsList, subject => {
    const { value, coursePrimaryGradeIds } = subject || {};

    const gradeId =
      _.size(coursePrimaryGradeIds) === 1
        ? _.first(coursePrimaryGradeIds)
        : yearGroupGradeId;

    if (gradeId && !_.includes(gradeIds, gradeId)) {
      gradeIds.push(gradeId);
    }

    subjectIds.push(value);
  });

  return {
    ids: subjectIds,
    gradingConfigFilters: {
      type: [SCORE_BASED, GRADING_VISIBILITY_SETTINGS],
      gradeIds,
    },
    gradingMethodologyFilters: {
      gradeIds,
      academicYearId,
    },
  };
};

export const getLearningCourseGradingConfigVariables = learningCourseList => {
  const learningCourseIds = [];

  _.forEach(learningCourseList, ({ value }) => {
    learningCourseIds.push(value);
  });

  return {
    ids: learningCourseIds,
    gradingConfigFilters: {
      type: [SCORE_BASED, GRADING_VISIBILITY_SETTINGS],
    },
  };
};

export const getGradingConfigAndVisibilitySettings = ({
  userType,
  subjectId,
  subjectsList,
  learningCourseId,
  yearGroupGradeId,
  gradingConfigsData,
  isCourseBasedGrading,
  curriculumVisibilitySettings,
  useCurriculumVisibilitySettings,
}) => {
  const groupTypeByUserType =
    userType === STUDENT ? "STUDENT_VISIBILITY" : "PARENT_VISIBILITY";

  const entityId = isCourseBasedGrading ? learningCourseId : subjectId;
  const entityObj = _.find(gradingConfigsData, { id: entityId });
  const gradingConfigurations = _.get(entityObj, "gradingConfigurations");
  let gradingMethodology = _.get(entityObj, "gradingMethodology");

  let filteredGradingConfigurations = gradingConfigurations;

  if (!isCourseBasedGrading) {
    const subject = _.find(subjectsList, { value: subjectId });
    const coursePrimaryGradeIds = _.get(subject, "coursePrimaryGradeIds", []);
    const gradeIdForSubject =
      _.size(coursePrimaryGradeIds) === 1
        ? _.first(coursePrimaryGradeIds)
        : yearGroupGradeId;

    const gradingMethodologies = _.get(entityObj, "gradingMethodologies");
    gradingMethodology = _.find(gradingMethodologies, ({ entityDetail }) => {
      return (
        entityDetail?.gradeId === gradeIdForSubject &&
        entityDetail?.subjectId === subjectId
      );
    });

    filteredGradingConfigurations = _.filter(
      gradingConfigurations,
      ({ entityDetail }) => {
        const { gradeId, subjectId: configSubjectId } = entityDetail || {};
        return gradeIdForSubject === gradeId && subjectId === configSubjectId;
      }
    );
  }

  const gradingConfig = _.find(filteredGradingConfigurations, {
    type: SCORE_BASED,
  });
  const gradingMethodologyTypes = _.get(
    gradingMethodology,
    "gradingMethodologyTypes"
  );

  const visibilitySettingsConfigs = useCurriculumVisibilitySettings
    ? _.compact(_.castArray(curriculumVisibilitySettings))
    : _.filter(filteredGradingConfigurations, {
        type: GRADING_VISIBILITY_SETTINGS,
      });

  const visibilitySettingsByType = _.reduce(
    visibilitySettingsConfigs,
    (result, config) => {
      const { settings } = config || {};

      _.forEach(settings, setting => {
        const { groupType, uid, value } = setting || {};

        if (groupType !== groupTypeByUserType) {
          return;
        }

        let parsedValue = {};

        if (value) {
          try {
            parsedValue = JSON.parse(value);
          } catch (e) {
            console.error(e);
          }
        }

        result[uid] = parsedValue;
      });

      return result;
    },
    {}
  );

  return {
    gradingConfig,
    gradingMethodologyTypes,
    visibilitySettings: visibilitySettingsByType,
  };
};

export const getRowTermWiseGradeScales = ({
  gradeScales,
  subjectsList,
  yearGroupGradeId,
  gradingConfigsData,
  isCourseBasedGrading,
}) => {
  const rowTermWiseGradeScales = {};

  if (_.isEmpty(gradingConfigsData)) {
    return rowTermWiseGradeScales;
  }

  _.forEach(gradingConfigsData, ({ id: rowId }) => {
    let gradeIdForSubject;

    if (!isCourseBasedGrading) {
      const subject = _.find(subjectsList, { value: rowId });
      const coursePrimaryGradeIds = _.get(subject, "coursePrimaryGradeIds", []);

      gradeIdForSubject =
        _.size(coursePrimaryGradeIds) === 1
          ? _.first(coursePrimaryGradeIds)
          : yearGroupGradeId;
    }

    _.forEach(gradeScales, ({ node }) => {
      const {
        id: gradeScaleId,
        type,
        displayName,
        templateName,
        dependentGradeScales,
        gradeScaleEntityDetails,
      } = node || {};

      _.forEach(
        gradeScaleEntityDetails,
        ({ entityDetail, gradingConfiguration }) => {
          const { learningCourseId, subjectId, gradeId, academicTermId } =
            entityDetail || {};

          const entityId = isCourseBasedGrading ? learningCourseId : subjectId;

          const rowCheck = isCourseBasedGrading
            ? entityId === rowId
            : entityId === rowId && gradeId === gradeIdForSubject;

          if (rowCheck) {
            if (!rowTermWiseGradeScales[rowId]) {
              rowTermWiseGradeScales[rowId] = {};
            }

            if (!rowTermWiseGradeScales[rowId][academicTermId]) {
              rowTermWiseGradeScales[rowId][academicTermId] = [];
            }

            const finalGradeCalculationSettings = getFinalGradeCalculationSettings(
              {
                gradeScale: node,
                termId: academicTermId,
                subjectId,
                gradeId,
                learningCourseId,
              }
            );

            const finalGradeCalculationMethodology = _.get(
              finalGradeCalculationSettings,
              FINAL_GRADE_CALCULATION_METHODOLOGY_UID
            );

            rowTermWiseGradeScales[rowId][academicTermId].push({
              id: gradeScaleId,
              type,
              displayName,
              templateName,
              dependentGradeScales,
              finalGradeCalculationMethodology,
            });
          }
        }
      );
    });
  });

  return rowTermWiseGradeScales;
};

export const addGradingMethodologyTypes = ({
  items,
  gradingConfigsData,
  yearGroupGradeId,
  isLearningCourse = false,
}) => {
  return _.map(items, item => {
    const { coursePrimaryGradeIds, value } = item || {};
    const config = _.find(gradingConfigsData, { id: value });

    if (isLearningCourse) {
      return {
        ...item,
        gradingMethodologyTypes: _.get(
          config,
          "gradingMethodology.gradingMethodologyTypes"
        ),
      };
    }

    const gradeIdForSubject =
      _.size(coursePrimaryGradeIds) === 1
        ? _.first(coursePrimaryGradeIds)
        : yearGroupGradeId;

    const gradingMethodologies = _.get(config, "gradingMethodologies");
    const gradingMethodologyTypes = _.find(
      gradingMethodologies,
      ({ entityDetail }) => {
        return (
          entityDetail?.gradeId === gradeIdForSubject &&
          entityDetail?.subjectId === value
        );
      }
    )?.gradingMethodologyTypes;

    return {
      ...item,
      gradingMethodologyTypes,
    };
  });
};

export const filterItemsByGradingMethodology = ({
  items,
  validMethodologyTypes,
  isUsingNewGradingSetupInCourses,
}) => {
  if (!isUsingNewGradingSetupInCourses) {
    return items;
  }

  return _.filter(items, item => {
    const gradingMethodologyTypes = _.get(item, "gradingMethodologyTypes", []);
    return _.some(validMethodologyTypes, methodologyType =>
      _.includes(gradingMethodologyTypes, methodologyType)
    );
  });
};

export const getSubjectLevelTags = subject => {
  const tags = _.get(subject, "tags", []);
  if (_.isEmpty(tags)) {
    return null;
  }
  const tagElements = _.map(tags, (tag, index) => {
    const tagId = _.get(tag, "id");
    const tagLabel = _.get(tag, "label", "");
    const tagKey = tagId || `${tagLabel || "tag"}-${index}`;
    return (
      <Tag key={tagKey} size={"xx-small"} color="neutral">
        {tagLabel}
      </Tag>
    );
  });
  return (
    <div
      style={{
        flexShrink: 0,
      }}
    >
      {tagElements}
    </div>
  );
};

// * Generating Learning couse options
export const generateLearningCourseOptions = learningCourseList => {
  return _.map(learningCourseList, learningCourse => {
    const { label, value, isArchived, displayMetadata } = learningCourse;

    const { color, name, AvatarIcon, iconType } = getAvatarData({
      id: value,
      title: label,
      profileImageData: displayMetadata,
    });

    return {
      id: value,
      key: value,
      label,
      value,
      isArchived,
      gradingPeriodIds: learningCourse?.gradingPeriodIds,
      labelSuffix: getSubjectLevelTags(learningCourse),
      icon: (
        <Avatar
          icon={<AvatarIcon size="small" />}
          name={name}
          size="small"
          shape="square"
          theme="light"
          color={color}
          iconType={iconType}
          dsVersion="2.0"
        />
      ),
    };
  });
};

export const getGradeScalesVariables = ({
  subjectsList,
  curriculumType,
  yearGroupGradeId,
  learningCourseList,
  curriculumProgramId,
  isCourseBasedGrading,
}) => {
  let entityType;
  const entityDetails = [];

  if (isCourseBasedGrading) {
    entityType = "LEARNING_COURSE";

    _.forEach(learningCourseList, ({ value }) => {
      entityDetails.push({
        learningCourseId: value,
      });
    });
  } else {
    entityType = "SUBJECT_GRADE_MAP";

    _.forEach(subjectsList, subject => {
      const { value, coursePrimaryGradeIds } = subject || {};

      const gradeId =
        _.size(coursePrimaryGradeIds) === 1
          ? _.first(coursePrimaryGradeIds)
          : yearGroupGradeId;

      if (value && gradeId) {
        entityDetails.push({
          subjectId: value,
          gradeId,
        });
      }
    });
  }

  const types = ["REGULAR", "DEPENDENT"];

  if (curriculumType === CURRICULUM_TYPE_MYP) {
    types.push(MYP_CRITERIA_TOTAL, MYP_IB_DEFINED, MYP_FINAL_GRADE);
  }
  if (curriculumType === CURRICULUM_TYPE_DP) {
    types.push(DP_IB_DEFINED);
  }

  return {
    id: curriculumProgramId,
    gradeScaleFilters: {
      types,
      isArchived: false,
    },
    gradeScaleEntityDetailFilters: {
      entityType,
      entityDetails,
    },
  };
};

export const getDynamicPlannerElementTitle = ({
  t,
  key,
  plannerElementSets,
  curriculumType,
  localeLabel,
}) => {
  switch (key) {
    case ELEMENT_TYPE_GRADING_PERIOD_OVERVIEW: {
      return t("progressSummary:final_grades");
    }

    case STUDENT_STANDARDS_GRADEBOOK: {
      const labelPath = `${STANDARDS_TYPES_BY_CURRICULUM[curriculumType]}.label`;
      return (
        _.get(plannerElementSets, labelPath, "") || t("common:standard_plural")
      );
    }
    default: {
      return _.get(plannerElementSets, `${key}.label`) || localeLabel;
    }
  }
};
